import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Select from 'react-select';
import { createUserTipDefinition, updateUserTipDefinition } from '../../api/user_tip_definitions';
import { fetchUsers } from '../../api/users';
import ImageUploader from '../../ImageUpload/ImageUploader';
import UserSearchPicker from '../General/UserSearchPicker';
import ShopmyImage from '../General/ShopmyImage';
import {
  CRITERIA_TYPES,
  CRITERIA_OPTIONS,
  AUDIENCE_MATCH_OPTIONS,
  PLATFORM_OPTIONS,
  PRIORITY_HINT
} from '../../constants/user_tip_constants';
import './UserTipDefinitions.scss';

const searchAudienceUsers = async query => {
  const data = await fetchUsers({ search: query, limit: 10 });
  return data.users || [];
};

const TAG_SEARCH_DEBOUNCE_MS = 200;
const MAX_TAG_OPTIONS = 50;
const isSafeWebUrl = value => {
  try {
    const { protocol } = new URL(value);
    return ['http:', 'https:'].includes(protocol);
  } catch {
    return false;
  }
};
const parseDeepLinkDataJson = value => {
  if (!value.trim()) return { deepLinkData: null };
  try {
    const deepLinkData = JSON.parse(value);
    if (!deepLinkData || typeof deepLinkData !== 'object' || Array.isArray(deepLinkData)) {
      return { error: 'Mobile deep-link data must be a JSON object.' };
    }
    return { deepLinkData };
  } catch {
    return { error: 'Mobile deep-link data must be valid JSON.' };
  }
};
const getFormError = form => {
  if (!form.name.trim()) return 'Add an internal name.';
  if (!PLATFORM_OPTIONS.some(option => option.value === form.platform)) return 'Choose a platform.';
  const priority = Number(form.priority);
  if (!Number.isInteger(priority) || priority < 1 || priority > 5) return 'Choose a priority from 1 to 5.';
  if (form.criteria === CRITERIA_TYPES.USER_CREATION_AGE) {
    const minAgeDays = Number(form.minAgeDays);
    const maxAgeDays = form.maxAgeDays === '' ? null : Number(form.maxAgeDays);
    if (!Number.isInteger(minAgeDays) || minAgeDays < 0) return 'Add a valid minimum account age.';
    if (maxAgeDays !== null && (!Number.isInteger(maxAgeDays) || maxAgeDays < minAgeDays)) return 'Maximum account age must be at least the minimum.';
  }
  if (!form.tagIds.length && !form.selectedUsers.length) return 'Select at least one audience tag or user.';
  if (!form.tipText.trim()) return 'Add tip text.';
  if (!form.hasButton) return null;
  if (!form.buttonLabel.trim()) return 'Add a button label.';
  if (form.buttonBehavior === 'popup') {
    if (!form.popupHeadline.trim()) return 'Add a popup headline.';
    if (!form.popupText.trim()) return 'Add popup text.';
    return null;
  }
  const webUrl = form.buttonWebUrl.trim();
  const { deepLinkData, error: deepLinkDataError } = parseDeepLinkDataJson(form.buttonDeepLinkDataJson);
  if (deepLinkDataError) return deepLinkDataError;
  if (!webUrl && !deepLinkData) return 'Add a web URL or mobile deep-link data for the button.';
  if (webUrl && !isSafeWebUrl(webUrl)) return 'Add a valid http or https web URL.';
  return null;
};

const emptyForm = {
  name: '',
  isActive: false,
  platform: 'MOBILE',
  priority: 5,
  criteria: CRITERIA_TYPES.NEXT_APP_OPEN,
  minAgeDays: '',
  maxAgeDays: '',
  tagIds: [],
  excludeTagIds: [],
  selectedUsers: [],
  match: 'any',
  tipText: '',
  tipImage: '',
  bold: false,
  hasButton: false,
  buttonLabel: '',
  buttonBehavior: 'url',
  buttonWebUrl: '',
  buttonDeepLinkDataJson: '',
  popupHeadline: '',
  popupText: '',
  popupImage: ''
};

const formFromDefinition = (definition, asDuplicate) => ({
  name: asDuplicate ? `${definition.name} (Copy)` : definition.name,
  isActive: asDuplicate ? false : !!definition.isActive,
  platform: definition.platform || 'BOTH',
  priority: definition.priority,
  criteria: definition.criteria,
  minAgeDays: definition.criteriaConfig?.minAgeDays ?? '',
  maxAgeDays: definition.criteriaConfig?.maxAgeDays ?? '',
  tagIds: definition.audience?.tagIds || [],
  excludeTagIds: definition.audience?.excludeTagIds || [],
  selectedUsers:
    definition.audienceUsers ||
    (definition.audience?.userIds || []).map(userId => ({ id: userId, name: `User ${userId}`, username: null })),
  match: definition.audience?.match || 'any',
  tipText: definition.tipText || '',
  tipImage: definition.ui?.image || '',
  bold: !!definition.ui?.bold,
  hasButton: !!definition.ui?.button,
  buttonLabel: definition.ui?.button?.label || '',
  buttonBehavior: definition.ui?.button?.popup ? 'popup' : 'url',
  buttonWebUrl: definition.ui?.button?.webUrl || '',
  buttonDeepLinkDataJson: definition.ui?.button?.deepLinkData ? JSON.stringify(definition.ui.button.deepLinkData, null, 2) : '',
  popupHeadline: definition.ui?.button?.popup?.headline || '',
  popupText: definition.ui?.button?.popup?.text || '',
  popupImage: definition.ui?.button?.popup?.image || ''
});

/** Mirrors the creator banner's title/body split — first newline if present, else first sentence. */
const splitCopy = copy => {
  if (!copy) return { title: '', body: '' };
  const nlIndex = copy.indexOf('\n');
  if (nlIndex !== -1) return { title: copy.slice(0, nlIndex).trim(), body: copy.slice(nlIndex + 1).trim() };
  const match = copy.match(/^(.+?[.!?—])\s*(.*)$/s);
  if (match) return { title: match[1], body: match[2] };
  return { title: copy, body: '' };
};

const UserTipDefinitionModal = props => {
  const { definition, asDuplicate, allTags, onSave, onClose } = props;

  const [form, setForm] = useState(definition ? formFromDefinition(definition, asDuplicate) : emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [tagSearchInput, setTagSearchInput] = useState('');
  const [debouncedTagSearch, setDebouncedTagSearch] = useState('');
  const [excludeTagSearchInput, setExcludeTagSearchInput] = useState('');
  const [debouncedExcludeTagSearch, setDebouncedExcludeTagSearch] = useState('');
  const [activeImageField, setActiveImageField] = useState(null);
  const tagDebounceRef = useRef(null);
  const excludeTagDebounceRef = useRef(null);

  const isEdit = !!definition && !asDuplicate;
  const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  useEffect(() => {
    if (tagDebounceRef.current) clearTimeout(tagDebounceRef.current);
    tagDebounceRef.current = setTimeout(() => {
      setDebouncedTagSearch(tagSearchInput);
      tagDebounceRef.current = null;
    }, TAG_SEARCH_DEBOUNCE_MS);
    return () => {
      if (tagDebounceRef.current) clearTimeout(tagDebounceRef.current);
    };
  }, [tagSearchInput]);

  useEffect(() => {
    if (excludeTagDebounceRef.current) clearTimeout(excludeTagDebounceRef.current);
    excludeTagDebounceRef.current = setTimeout(() => {
      setDebouncedExcludeTagSearch(excludeTagSearchInput);
      excludeTagDebounceRef.current = null;
    }, TAG_SEARCH_DEBOUNCE_MS);
    return () => {
      if (excludeTagDebounceRef.current) clearTimeout(excludeTagDebounceRef.current);
    };
  }, [excludeTagSearchInput]);

  const allTagOptions = useMemo(
    () => allTags.map(tag => ({ value: tag.id, label: `${tag.value || `Tag ${tag.id}`} (${tag.count || 0})` })),
    [allTags]
  );
  const tagOptions = useMemo(() => {
    const query = debouncedTagSearch.trim().toLowerCase();
    if (!query) return [];
    return allTagOptions
      .filter(option => !form.excludeTagIds.includes(option.value) && option.label.toLowerCase().includes(query))
      .slice(0, MAX_TAG_OPTIONS);
  }, [allTagOptions, debouncedTagSearch, form.excludeTagIds]);
  const excludeTagOptions = useMemo(() => {
    const query = debouncedExcludeTagSearch.trim().toLowerCase();
    if (!query) return [];
    return allTagOptions
      .filter(option => !form.tagIds.includes(option.value) && option.label.toLowerCase().includes(query))
      .slice(0, MAX_TAG_OPTIONS);
  }, [allTagOptions, debouncedExcludeTagSearch, form.tagIds]);

  const buildPayload = () => ({
    name: form.name.trim(),
    isActive: form.isActive,
    platform: form.platform,
    priority: Number(form.priority),
    criteria: form.criteria,
    criteriaConfig:
      form.criteria === CRITERIA_TYPES.USER_CREATION_AGE
        ? { minAgeDays: Number(form.minAgeDays), maxAgeDays: form.maxAgeDays === '' ? null : Number(form.maxAgeDays) }
        : {},
    audience: {
      tagIds: form.tagIds,
      userIds: form.selectedUsers.map(user => user.id),
      excludeTagIds: form.excludeTagIds,
      match: form.match
    },
    tipText: form.tipText.trim(),
    ui: {
      bold: form.bold,
      image: form.tipImage.trim() || null,
      button: form.hasButton
        ? {
            label: form.buttonLabel.trim(),
            webUrl: form.buttonBehavior === 'url' ? form.buttonWebUrl.trim() || null : null,
            deepLinkData: form.buttonBehavior === 'url' ? parseDeepLinkDataJson(form.buttonDeepLinkDataJson).deepLinkData : null,
            popup:
              form.buttonBehavior === 'popup'
                ? {
                    headline: form.popupHeadline.trim(),
                    text: form.popupText.trim(),
                    image: form.popupImage.trim() || null
                  }
                : null
          }
        : null
    }
  });

  const handleSave = async () => {
    const formError = getFormError(form);
    if (formError) return setError(formError);

    setSaving(true);
    setError(null);
    try {
      const payload = buildPayload();
      const saved = isEdit ? await updateUserTipDefinition(definition.id, payload) : await createUserTipDefinition(payload);
      onSave(saved);
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to save the tip. Please try again.');
      setSaving(false);
    }
  };
  const saveImage = url => {
    if (activeImageField) setField(activeImageField, url || '');
  };
  const renderImageField = (field, label) => (
    <div className='utd-image-field'>
      <label>{label}</label>
      <div className='utd-image-field-content'>
        {form[field] && <ShopmyImage source={form[field]} alt={label} className='utd-image-thumbnail' />}
        <button type='button' className='utd-btn utd-btn--secondary' onClick={() => setActiveImageField(field)}>
          {form[field] ? 'Change Image' : 'Add Image'}
        </button>
        {form[field] && (
          <button type='button' className='utd-image-remove' onClick={() => setField(field, '')}>
            Remove
          </button>
        )}
      </div>
    </div>
  );

  // Preview
  const previewCopy = form.tipText.trim() || 'Your tip copy will appear here.';
  const { title, body } = splitCopy(previewCopy);
  const previewButtonLabel = form.hasButton ? form.buttonLabel.trim() || 'Button' : null;

  return (
    <>
      <div className='utd-modal-backdrop' onMouseDown={e => e.target === e.currentTarget && onClose()}>
        <div className='utd-modal'>
        <div className='utd-modal-header'>
          <h2>{isEdit ? 'Edit User Tip' : 'New User Tip'}</h2>
          <button className='utd-modal-close' onClick={onClose}>
            ×
          </button>
        </div>

        <div className='utd-modal-body'>
          {error && <div className='utd-modal-error'>{error}</div>}

          <div className='utd-form-row'>
            <label>Name *</label>
            <input type='text' value={form.name} onChange={e => setField('name', e.target.value)} placeholder='Internal label, e.g. Spring Gifting Push' />
          </div>

          <div className='utd-form-grid'>
            <div className='utd-form-row'>
              <label>Priority</label>
              <input type='number' min='1' max='5' value={form.priority} onChange={e => setField('priority', e.target.value)} />
              <span className='utd-field-hint'>{PRIORITY_HINT}</span>
            </div>
            <div className='utd-form-row'>
              <label>Platform</label>
              <select value={form.platform} onChange={e => setField('platform', e.target.value)}>
                {PLATFORM_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className='utd-form-grid'>
            <div className='utd-form-row'>
              <label>Show When</label>
              <select value={form.criteria} onChange={e => setField('criteria', e.target.value)}>
                {CRITERIA_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            {form.criteria === CRITERIA_TYPES.USER_CREATION_AGE && (
              <div className='utd-form-row'>
                <label>Account Age (days)</label>
                <div className='utd-age-bounds'>
                  <input type='number' min='0' value={form.minAgeDays} onChange={e => setField('minAgeDays', e.target.value)} placeholder='Min' />
                  <span>to</span>
                  <input type='number' min='0' value={form.maxAgeDays} onChange={e => setField('maxAgeDays', e.target.value)} placeholder='No max' />
                </div>
                <span className='utd-field-hint'>Inclusive bounds. Leave max blank for no upper bound.</span>
              </div>
            )}
          </div>

          <div className='utd-form-row'>
            <label>Audience Tags</label>
            <span className='utd-field-hint'>Select tags, specific users below, or both.</span>
            <Select
              className='utd-tag-select'
              classNamePrefix='utd-tag-select'
              placeholder='Search creator tags...'
              options={tagOptions}
              value={allTagOptions.filter(option => form.tagIds.includes(option.value))}
              onChange={options => setField('tagIds', (options || []).map(option => option.value))}
              inputValue={tagSearchInput}
              onInputChange={setTagSearchInput}
              filterOption={() => true}
              isMulti
              isClearable
              noOptionsMessage={() => (debouncedTagSearch.trim() ? 'No matching tags' : 'Type to search tags')}
              menuPortalTarget={document.body}
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            />
            <div className='utd-match-options'>
              {AUDIENCE_MATCH_OPTIONS.map(opt => (
                <label key={opt.value} className='utd-radio-label'>
                  <input type='radio' checked={form.match === opt.value} onChange={() => setField('match', opt.value)} />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div className='utd-form-row'>
            <label>Exclude Tags</label>
            <span className='utd-field-hint'>Users with any selected exclusion tag will not receive this tip.</span>
            <Select
              className='utd-tag-select'
              classNamePrefix='utd-tag-select'
              placeholder='Search tags to exclude...'
              options={excludeTagOptions}
              value={allTagOptions.filter(option => form.excludeTagIds.includes(option.value))}
              onChange={options => setField('excludeTagIds', (options || []).map(option => option.value))}
              inputValue={excludeTagSearchInput}
              onInputChange={setExcludeTagSearchInput}
              filterOption={() => true}
              isMulti
              isClearable
              noOptionsMessage={() => (debouncedExcludeTagSearch.trim() ? 'No matching tags' : 'Type to search tags')}
              menuPortalTarget={document.body}
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            />
          </div>

          <div className='utd-form-row'>
            <UserSearchPicker
              selectedUsers={form.selectedUsers}
              onChange={users => setField('selectedUsers', users)}
              searchUsers={searchAudienceUsers}
              label='Specific Users'
              placeholder='Search by name, username, email, or user ID...'
              showUserIds
            />
          </div>

          <div className='utd-form-row'>
            <label>Tip Text *</label>
            <textarea value={form.tipText} onChange={e => setField('tipText', e.target.value)} placeholder='Tip copy shown to creators' rows={3} />
          </div>
          {renderImageField('tipImage', 'Tip Image')}

          <div className='utd-form-grid'>
            <div className='utd-form-row'>
              <label>Styling</label>
              <label className='utd-checkbox-label'>
                <input type='checkbox' checked={form.bold} onChange={() => setField('bold', !form.bold)} />
                Bold the full copy
              </label>
              <label className='utd-checkbox-label'>
                <input type='checkbox' checked={form.hasButton} onChange={() => setField('hasButton', !form.hasButton)} />
                Include a button
              </label>
            </div>
            {form.hasButton && (
              <div className='utd-form-row'>
                <label>Button</label>
                <input type='text' value={form.buttonLabel} onChange={e => setField('buttonLabel', e.target.value)} placeholder='Button label' maxLength={60} />
                <select value={form.buttonBehavior} onChange={e => setField('buttonBehavior', e.target.value)}>
                  <option value='url'>Open URL</option>
                  <option value='popup'>Open Popup</option>
                </select>
                {form.buttonBehavior === 'url' ? (
                  <>
                    <input
                      type='url'
                      value={form.buttonWebUrl}
                      onChange={e => setField('buttonWebUrl', e.target.value)}
                      placeholder='Web URL (https://...)'
                    />
                    <textarea
                      value={form.buttonDeepLinkDataJson}
                      onChange={e => setField('buttonDeepLinkDataJson', e.target.value)}
                      placeholder={'Mobile deepLinkData JSON, e.g. {"tab":"links"}'}
                      rows={5}
                    />
                    <span className='utd-field-hint'>Add a web URL, a mobile deep-link object, or both.</span>
                  </>
                ) : (
                  <>
                    <input
                      type='text'
                      value={form.popupHeadline}
                      onChange={e => setField('popupHeadline', e.target.value)}
                      placeholder='Popup headline'
                    />
                    <textarea
                      value={form.popupText}
                      onChange={e => setField('popupText', e.target.value)}
                      placeholder='Popup text'
                      rows={3}
                    />
                    {renderImageField('popupImage', 'Popup Image')}
                  </>
                )}
              </div>
            )}
          </div>

          <div className='utd-preview-section'>
            <div className='utd-preview-header'>
              <label>Preview</label>
            </div>
            <div className='utd-preview-banner'>
              {form.tipImage ? (
                <ShopmyImage source={form.tipImage} alt='Tip' className='utd-preview-image' />
              ) : (
                <div className='utd-preview-logo'>S</div>
              )}
              <div className={cn('utd-preview-text', { bold: form.bold })}>
                {form.bold ? (
                  <span className='utd-preview-title'>{previewCopy}</span>
                ) : (
                  <>
                    <span className='utd-preview-title'>{title}</span>
                    {body && <span className='utd-preview-body'>{body}</span>}
                  </>
                )}
              </div>
              <div className='utd-preview-actions'>
                {previewButtonLabel && (
                  <button type='button' className='utd-preview-cta'>
                    {previewButtonLabel}
                  </button>
                )}
                <span className='utd-preview-badge'>New</span>
                <span className='utd-preview-dismiss'>×</span>
              </div>
            </div>
            {form.hasButton && form.buttonBehavior === 'popup' && (
              <div className='utd-popup-preview'>
                {form.popupImage && <ShopmyImage source={form.popupImage} alt='Popup' className='utd-popup-preview-image' />}
                <div>
                  <div className='utd-popup-preview-headline'>{form.popupHeadline || 'Popup headline'}</div>
                  <div className='utd-popup-preview-text'>{form.popupText || 'Popup text will appear here.'}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className='utd-modal-footer'>
          <button className='utd-btn utd-btn--secondary' onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className='utd-btn utd-btn--primary' onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Tip'}
          </button>
        </div>
        </div>
      </div>
      {activeImageField && (
        <ImageUploader
          key={activeImageField}
          isVisible
          setIsVisible={isVisible => !isVisible && setActiveImageField(null)}
          initialImageUrl={form[activeImageField] || ''}
          onSaveCallback={saveImage}
          allowCrop={false}
        />
      )}
    </>
  );
};

UserTipDefinitionModal.propTypes = {
  definition: PropTypes.object,
  asDuplicate: PropTypes.bool,
  allTags: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

export default UserTipDefinitionModal;
